/**
 * Dependency imports
 */
import React from 'react';
import {
  Anchor,
  Box,
  BoxProps,
  Divider,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  Alert,
  PasswordInput,
  LoadingOverlay,
} from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Joi from 'joi';

/**
 * Local imports
 */
import Button from '@/components/Button';
import FAIcon from '@/components/FAIcon';
import { useEmailSignup } from '@/hooks/use-email-signup';
import { useProviderAuth } from '@/hooks/use-provider-auth';
import EmailSVG from '../../../public/img/icons/email.svg';
import GoogleSVG from '../../../public/img/icons/social/google.svg';

/**
 * Type definitions
 */
interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
}

interface Props {
  onLoginRequest?: () => void;
  onCloseRequest?: () => void;
  onSignupSuccess?: () => void;
  children?: React.ReactNode;
}

export type SignupFormProps = Props & BoxProps;

/**
 * Styled components
 */
const Root = styled(Box, {
  shouldForwardProp: (prop: PropertyKey) => ![''].includes(prop.toString()),
})<Partial<SignupFormProps>>((props) => css`
  position: relative;
  min-height: 400px;
`);

/**
 * Password requirement component
 */
const PasswordRequirement = ({ meets, label }: { meets: boolean; label: string }) => {
  return (
    <Text
      c={meets ? 'teal' : 'red'}
      style={{ display: 'flex', alignItems: 'center' }}
      mt={7}
      size="sm"
    >
      <FAIcon
        name={meets ? 'check' : 'xmark'}
        size={14}
        style={{ marginRight: '10px' }}
      />
      <Box ml={10}>{label}</Box>
    </Text>
  );
};

/**
 * Password requirements check
 */
const requirements = [
  { re: /[0-9]/, label: 'Includes number' },
  { re: /[a-z]/, label: 'Includes lowercase letter' },
  { re: /[A-Z]/, label: 'Includes uppercase letter' },
  { re: /[@$!%*#?&]/, label: 'Includes special character (@$!%*#?&)' },
  { re: /.{8,}/, label: 'At least 8 characters' },
];

/**
 * SignupForm Component
 */
const SignupForm: React.FC<SignupFormProps> = ({
  onLoginRequest,
  onCloseRequest,
  onSignupSuccess,
  ...props
}) => {
  const [emailFormVisible, setEmailFormVisible] = React.useState(false);
  const [emailSignup, emailSignupResponse] = useEmailSignup();
  const [googleAuth, googleAuthResponse] = useProviderAuth('google');
  const [popoverOpened, setPopoverOpened] = React.useState(false);

  // Enhanced password validation regex
  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,30}$/;

  const schema = Joi.object({
    email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        'string.empty': 'Please enter an e-mail address',
        'string.email': 'Please enter a valid e-mail address',
      }),
    password: Joi.string()
      .min(8)
      .max(30)
      .pattern(passwordRegex)
      .required()
      .messages({
        'string.empty': 'Please enter a password',
        'string.min': 'Password must be at least 8 characters',
        'string.max': 'Password must be 30 characters or less',
        'string.pattern.base':
          'Password must contain letters, numbers, and special characters',
      }),
    confirmPassword: Joi.any()
      .valid(Joi.ref('password'))
      .required()
      .messages({
        'any.only': 'Passwords do not match',
        'any.required': 'Please confirm your password',
      }),
  });

  const form = useForm<SignupFormData>({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
    validate: joiResolver(schema),
  });

  const pending =
    googleAuthResponse.status === 'pending' ||
    emailSignupResponse.status === 'pending';

  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={requirement.label}
      meets={requirement.re.test(form.values.password)}
    />
  ));

  const handleSubmit = async (values: SignupFormData) => {
    try {
      await emailSignup(values.email, values.password);
      onSignupSuccess?.();
    } catch (error) {
      console.error('Signup error:', error);
      // Error will be handled by getErrorMessage
    }
  };

  const getErrorMessage = (error: any) => {
    // console.log('Error details:', error); // For debugging

    // Handle known error cases with more specific messages
    const errorMessages: Record<string, { title: string; message: string; action?: string }> = {
      'auth/email-already-in-use': {
        title: 'Email Already Registered',
        message: 'An account already exists with this email address. Please use a different email or login to your existing account.',
        action: 'login'
      },
      'auth/invalid-email': {
        title: 'Invalid Email',
        message: 'Please enter a valid email address.',
      },
      'auth/weak-password': {
        title: 'Weak Password',
        message: 'Your password is too weak. Please choose a stronger password that meets all requirements.',
      },
      'auth/network-request-failed': {
        title: 'Network Error',
        message: 'Unable to connect to the server. Please check your internet connection and try again.',
      },
      'auth/too-many-requests': {
        title: 'Too Many Attempts',
        message: 'Too many unsuccessful attempts. Please wait a few minutes before trying again.',
      }
    };

    // Get error code from the error object
    const errorCode = error?.cause || 'unknown';
    // console.log('Error code:', errorCode); // For debugging

    // Get error info from our mapping or use default
    const errorInfo = errorMessages[errorCode] || {
      title: 'Email Already Exists',
      message: 'This email address is already registered. Please try logging in or use a different email address.',
      action: 'login'
    };

    return (
      <Alert
        icon={<FAIcon name="exclamation-circle" size={16} />}
        title={errorInfo.title}
        color="red"
        radius="md"
        styles={{
          title: {
            fontWeight: 600
          }
        }}
      >
        <Stack gap="xs">
          <Text size="sm">{errorInfo.message}</Text>

          {errorInfo.action === 'login' && (
            <Group justify="flex-start">
              <Button
                variant="outline"
                color="primary"
                size="small"
                leftSection={<FAIcon name="arrow-right-long" size={14} />}
                onClick={onLoginRequest}
                transition={false}
                hasArrow
                expandOnHover={false}
                component='button'
              >
                Go to Login
              </Button>
            </Group>
          )}
        </Stack>
      </Alert>
    );
  };

  if (emailSignupResponse.status === 'complete') {
    return (
      <Root {...props} p={30}>
        <Title size={32}>Almost There!</Title>
        <Text opacity={0.7} fz={14} mt={5}>
          Thank you for signing up! We have sent you an e-mail.
        </Text>
        <Divider my={20} />
        <Alert
          icon={<FAIcon name="info-circle" size={16} />}
          title="Verification Required"
          color="blue"
          radius="md"
        >
          Please check your inbox and follow the instructions provided to verify
          your e-mail address.
        </Alert>
        <Box mt={30}>
          <Button
            component="button"
            fullwidth
            transition={false}
            color="dark"
            onClick={onCloseRequest}
          >
            Close
          </Button>
        </Box>
      </Root>
    );
  }

  return (
    <Root {...props} p={30}>
      <LoadingOverlay visible={pending} />

      <Title size={32}>Create a New Account</Title>
      <Text opacity={0.7} fz={14} mt={5}>
        Please enter your details to continue
      </Text>
      <Divider my={20} />

      {emailFormVisible ? (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              required
              label={<strong>Email</strong>}
              placeholder="your.email@example.com"
              disabled={pending}
              {...form.getInputProps('email')}
              leftSection={<FAIcon name="envelope" size={14} />}
            />

            <PasswordInput
              required
              label={<strong>Password</strong>}
              placeholder="Create a strong password"
              onFocus={() => setPopoverOpened(true)}
              onBlur={() => setPopoverOpened(false)}
              disabled={pending}
              {...form.getInputProps('password')}
              leftSection={<FAIcon name="lock" size={14} />}
            />

            <PasswordInput
              required
              label={<strong>Confirm Password</strong>}
              placeholder="Re-enter your password"
              disabled={pending}
              {...form.getInputProps('confirmPassword')}
              leftSection={<FAIcon name="lock" size={14} />}
            />

            <Box>
              <Text size="sm" c="dimmed" mb={5}>
                Password requirements:
              </Text>
              {checks}
            </Box>

            {emailSignupResponse.status === 'error' && (
              <Box mt={10}>
                {getErrorMessage(emailSignupResponse.info)}
              </Box>
            )}

            <Button
              component="button"
              fullwidth
              transition={false}
              color="dark"
              type="submit"
              disabled={pending}
              loading={pending}
              leftSection={<FAIcon name="user" size={14} />}
            >
              Create My Account
            </Button>
          </Stack>
        </form>
      ) : (
        <Stack>
          <Button
            component="button"
            color="default"
            fullwidth
            radius="sm"
            transition={false}
            disabled={pending}
            onClick={() => setEmailFormVisible(true)}
          >
            <Group gap={10}>
              <Box w={16} style={{ display: 'flex', alignContent: 'center' }}>
                <EmailSVG />
              </Box>
              <Text fz={14} fw={500}>
                Sign up with Email
              </Text>
            </Group>
          </Button>

          <Button
            component="button"
            color="default"
            fullwidth
            radius="sm"
            transition={false}
            disabled={pending}
            onClick={googleAuth}
          >
            <Group gap={10}>
              <Box w={16} style={{ display: 'flex', alignContent: 'center' }}>
                <GoogleSVG />
              </Box>
              <Text fz={14} fw={500}>
                Sign up with Google
              </Text>
            </Group>
          </Button>
        </Stack>
      )}

      <Divider my={20} label={<Text>or</Text>} />

      <Text size="sm" ta="center">
        Already have an account? &nbsp;
        <Anchor c="dark" opacity={0.6} onClick={onLoginRequest}>
          Login
        </Anchor>
      </Text>

      <Text size="xs" c="dark" mt={20} mb={5}>
        By proceeding, you agree to the{' '}
        <Anchor
          href="https://www.iubenda.com/terms-and-conditions/12581878"
          target="_blank"
          rel="noopener noreferrer"
          c="gray.6"
        >
          Terms and Conditions
        </Anchor>{' '}
        and{' '}
        <Anchor
          href="https://www.iubenda.com/privacy-policy/12581878"
          target="_blank"
          rel="noopener noreferrer"
          c="gray.6"
        >
          Privacy Policy
        </Anchor>
      </Text>
    </Root>
  );
};

export default SignupForm;